import { ThunkAction } from "redux-thunk";
import { ReactElement } from "react";
import { Application } from "../../App/Models/Application";
import { IAppState, ApiRequest, ResponseError } from "../../Common";
import { endpoints } from "../../App/Constants/endpoints";
import { endpoints as topicEndpoints } from "../../Topic/Constants/endpoints";
import ResponseHandler from "../../App/ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { Company } from "../../Company/Models/Company";
import { StatusCode } from "../../App/Enums/StatusCode";
import { Topic } from "../../Topic/Models/Topic";
import { endpoints as companyEndpoints } from "../../Company/Constants/endpoints";
import { SubscriptionState } from "../../App";
import { startupConstants } from "../Constants/startupConstants";
import { BreadcrumbData } from "../../Layout";
import { TopicManagerAssignment } from "../../Topic/Models/TopicManagerAssignment";
import { UserTopicAssignment } from "../../Topic/Models/UserTopicAssignment";

export enum StartupActionType {
  SET_ACTIVE_APP = "GLOBAL_SET_ACTIVE_APP",
  SET_APPLICATIONS = "GLOBAL_SET_APPLICATIONS",
  SET_LOADING_APPLICATIONS = "GLOBAL_SET_LOADING_APPLICATIONS",
  SET_APP_LOADING_ERROR = "GLOBAL_SET_APP_LOADING_ERROR",

  SET_COMPANY = "GLOBAL_SET_COMPANY",
  SET_COMPANY_LOADING_ERROR = "GLOBAL_SET_COMPANY_LOADING_ERROR",
  SET_LOADING_COMPANY = "GLOBAL_SET_LOADING_COMPANY",

  SET_ACTIVE_TOPIC = "GLOBAL_SET_ACTIVE_TOPIC",
  SET_TOPICS = "GLOBAL_SET_TOPICS",
  SET_TOPICS_LOADING_ERROR = "GLOBAL_SET_TOPICS_LOADING_ERROR",
  SET_LOADING_TOPICS = "GLOBAL_SET_LOADING_TOPICS",
  ADD_TOPIC = "GLOBAL_ADD_TOPIC",
  UPDATE_TOPIC = "GLOBAL_UPDATE_TOPIC",
  SET_LOADING_TOPIC_MANAGERS = "GLOBAL_SET_LOADING_TOPIC_MANAGERS",
  SET_TOPIC_MANAGERS = "GLOBAL_SET_TOPIC_MANAGERS",
  SET_TOPIC_MANAGERS_ERROR = "GLOBAL_SET_TOPIC_MANAGERS_ERROR",
  SET_LOADING_TOPIC_USERS = "GLOBAL_SET_LOADING_TOPIC_USERS",
  SET_TOPIC_USERS = "GLOBAL_SET_TOPIC_USERS",
  SET_TOPIC_USERS_ERROR = "GLOBAL_SET_TOPIC_USERS_ERROR",

  SET_LOCATION = "GLOBAL_SET_PATH",
  SET_BREADCRUMB = "GLOBAL_SET_BREADCRUMB"
}

export interface ActionBase {
  type: StartupActionType;
}

export interface Action<Payload> extends ActionBase {
  payload: Payload;
}

export interface GetApplicationsArguments {
  appSubStates: Record<string, SubscriptionState>;
}

const getDefaultApp = (args: GetApplicationsArguments, apps: Application[]): Application | null => {
  const defaultAppSubstate = args.appSubStates[startupConstants.defaultAppCode];

  // make sure they have a valid subscription to the default app
  if (defaultAppSubstate === SubscriptionState.Active) {
    // return the default app if we can find it

    const defaultApp = apps.filter((a: Application) => {
      return a.rolePrefix === startupConstants.defaultAppCode;
    })[0];

    if (defaultApp) {
      return defaultApp;
    }
  }

  // the normal default app could not be found so find an app we have a subscription to
  for (let i = 0; i < apps.length; i++) {
    // check this app's substate
    const mySubState = args.appSubStates[apps[i].rolePrefix];

    if (mySubState === SubscriptionState.Active) {
      return apps[i];
    }
  }

  return null;
};

export const setPath = (path: string): Action<string> => {
  return {
    type: StartupActionType.SET_LOCATION,
    payload: path
  };
};

export const setActiveApp = (app: Application): Action<Application> => {
  return {
    type: StartupActionType.SET_ACTIVE_APP,
    payload: app
  };
};

export const setApplications = (apps: Application[]): Action<Application[]> => {
  return {
    type: StartupActionType.SET_APPLICATIONS,
    payload: apps
  };
};

export const setLoading = (): ActionBase => {
  return {
    type: StartupActionType.SET_LOADING_APPLICATIONS
  };
};

export const setAppLoadingError = (
  errorCode: number,
  errorDescription: string
): Action<ResponseError> => {
  return {
    type: StartupActionType.SET_APP_LOADING_ERROR,
    payload: {
      errorCode,
      errorDescription,
      errorResult: "Failed to load applications"
    }
  };
};

const setBreadcrumbData = (breadcrumbs: BreadcrumbData[]): Action<BreadcrumbData[]> => {
  return {
    type: StartupActionType.SET_BREADCRUMB,
    payload: breadcrumbs
  };
};

export const setBreadcrumbs = (
  breadcrumbs: Promise<BreadcrumbData[]>
): ThunkAction<void, IAppState, {}, any> => dispatch => {
  Promise.resolve(breadcrumbs).then(data => {
    dispatch(setBreadcrumbData(data));
  });
};

export const getApplications = (
  args: GetApplicationsArguments
): ThunkAction<void, IAppState, {}, any> => dispatch => {
  ApiRequest.fromPath(endpoints.applications.get())
    .withAuthHeader()
    .get()
    .then((response: Response) => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult) => {
          dispatch(setApplications(result.payload));
          // set default app
          const defaultApp = getDefaultApp(args, result.payload);
          if (defaultApp) {
            dispatch(setActiveApp(defaultApp));
          }
        },
        (result: ResponseResult) => {
          dispatch(setAppLoadingError(response.status, result.error.message));
        },
        () => {
          dispatch(setAppLoadingError(response.status, "An unknown error has occured"));
        }
      );
    })
    .catch(() => {
      dispatch(
        setAppLoadingError(StatusCode.Status503ServiceUnavailable, "Unable to contact server")
      );
    });
};

export const setCompany = (company: Company, callback: () => void): Action<Company> => {
  callback();
  return {
    type: StartupActionType.SET_COMPANY,
    payload: company
  };
};

export const setLoadingCompany = (): ActionBase => {
  return {
    type: StartupActionType.SET_LOADING_COMPANY
  };
};

export const setCompanyLoadingError = (
  errorCode: number,
  errorDescription: string
): Action<ResponseError> => {
  return {
    type: StartupActionType.SET_COMPANY_LOADING_ERROR,
    payload: {
      errorCode,
      errorDescription,
      errorResult: "Failed to load company"
    }
  };
};

export const getCompany = (
  username: string,
  callback?: (company: Company) => void
): ThunkAction<void, IAppState, {}, any> => dispatch => {
  ApiRequest.fromPath(companyEndpoints.companies.getByUsername(username))
    .withAuthHeader()
    .get()
    .then((response: Response) => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult<Company>) => {
          dispatch(
            setCompany(result.payload, () => {
              if (callback) {
                callback(result.payload);
              }
            })
          );
        },
        (result: ResponseResult) => {
          dispatch(setCompanyLoadingError(response.status, result.error.message));
        },
        () => {
          dispatch(setCompanyLoadingError(response.status, "An unknown error has occured"));
        }
      );
    })
    .catch(() => {
      dispatch(
        setCompanyLoadingError(StatusCode.Status503ServiceUnavailable, "Unable to contact server")
      );
    });
};

export const setTopics = (topics: Topic[]): Action<Topic[]> => {
  return {
    type: StartupActionType.SET_TOPICS,
    payload: topics
  };
};

export const setLoadingTopics = (): ActionBase => {
  return {
    type: StartupActionType.SET_LOADING_COMPANY
  };
};

export const setTopicsLoadingError = (
  errorCode: number,
  errorDescription: string
): Action<ResponseError> => {
  return {
    type: StartupActionType.SET_TOPICS_LOADING_ERROR,
    payload: {
      errorCode,
      errorDescription,
      errorResult: "Failed to load topics"
    }
  };
};

export const addTopic = (topic: Topic): Action<Topic> => {
  return {
    type: StartupActionType.ADD_TOPIC,
    payload: topic
  };
};

export const getTopics = (shortname: string): ThunkAction<void, IAppState, {}, any> => dispatch => {
  ApiRequest.fromPath(topicEndpoints.topics.getByCompany(shortname))
    .withAuthHeader()
    .get()
    .then(response => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult<Topic[]>) => {
          dispatch(setTopics(result.payload));
        },
        result => {
          dispatch(setTopicsLoadingError(response.status, result.error.message));
        },
        () => {
          dispatch(setTopicsLoadingError(response.status, "An unknown error has occured"));
        }
      );
    })
    .catch(() => {
      dispatch(
        setTopicsLoadingError(StatusCode.Status503ServiceUnavailable, "Unable to contact server")
      );
    });
};

export const updateTopics = (topics: Topic[]): Action<Topic[]> => {
  return {
    type: StartupActionType.UPDATE_TOPIC,
    payload: topics
  };
};

export const setActiveTopic = (topic: Topic): Action<Topic> => {
  return {
    type: StartupActionType.SET_ACTIVE_TOPIC,
    payload: topic
  };
};

export const setLoadingTopicManagers = (topicCode: string): Action<string> => {
  return {
    type: StartupActionType.SET_LOADING_TOPIC_MANAGERS,
    payload: topicCode
  };
};

export const setTopicManagers = (
  topicCode: string,
  managers: TopicManagerAssignment[]
): Action<{ topicCode: string; managers: TopicManagerAssignment[] }> => {
  return {
    type: StartupActionType.SET_TOPIC_MANAGERS,
    payload: {
      topicCode,
      managers
    }
  };
};

export const setTopicManagersError = (
  topicCode: string,
  errorCode: number,
  errorDescription: string
): Action<{ topicCode: string; error: ResponseError }> => {
  return {
    type: StartupActionType.SET_TOPIC_MANAGERS_ERROR,
    payload: {
      topicCode,
      error: {
        errorCode,
        errorDescription,
        errorResult: "Failed to load managers for topic"
      }
    }
  };
};

export const getTopicManagers = (
  code: string
): ThunkAction<void, IAppState, {}, any> => dispatch => {
  ApiRequest.fromPath(topicEndpoints.topics.getAssignedManagers(code))
    .withAuthHeader()
    .get()
    .then(response => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult<TopicManagerAssignment[]>) => {
          dispatch(setTopicManagers(code, result.payload));
        },
        result => {
          dispatch(setTopicManagersError(code, response.status, result.error.message));
        },
        () => {
          dispatch(setTopicManagersError(code, response.status, "An unknown error has occured"));
        }
      );
    })
    .catch(() => {
      dispatch(
        setTopicManagersError(
          code,
          StatusCode.Status503ServiceUnavailable,
          "Unable to contact server"
        )
      );
    });
};

export const setLoadingTopicUsers = (topicCode: string): Action<string> => {
  return {
    type: StartupActionType.SET_LOADING_TOPIC_USERS,
    payload: topicCode
  };
};

export const setTopicUsers = (
  topicCode: string,
  users: UserTopicAssignment[]
): Action<{ topicCode: string; users: UserTopicAssignment[] }> => {
  return {
    type: StartupActionType.SET_TOPIC_USERS,
    payload: {
      topicCode,
      users
    }
  };
};

export const setTopicUsersError = (
  topicCode: string,
  errorCode: number,
  errorDescription: string
): Action<{ topicCode: string; error: ResponseError }> => {
  return {
    type: StartupActionType.SET_TOPIC_USERS_ERROR,
    payload: {
      topicCode,
      error: {
        errorCode,
        errorDescription,
        errorResult: "Failed to load users for topic"
      }
    }
  };
};

export const getTopicUsers = (code: string): ThunkAction<void, IAppState, {}, any> => dispatch => {
  ApiRequest.fromPath(topicEndpoints.topics.getAssignedUsers(code))
    .withAuthHeader()
    .get()
    .then(response => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult<UserTopicAssignment[]>) => {
          dispatch(setTopicUsers(code, result.payload));
        },
        result => {
          dispatch(setTopicUsersError(code, response.status, result.error.message));
        },
        () => {
          dispatch(setTopicUsersError(code, response.status, "An unknown error has occured"));
        }
      );
    })
    .catch(() => {
      dispatch(
        setTopicUsersError(code, StatusCode.Status503ServiceUnavailable, "Unable to contact server")
      );
    });
};
